/**
 * AccessRolesDataSource
 * 
 * Manages role records with real-time WebSocket synchronization
 */

import { DataSource, FilterSchema } from "./DataSource";
import { BasicDataSourceDeps } from "../../platform/services/types/DataSourceDependencies";
import { queryGqlApi } from "../../graphql/queryGqlApi";
import { listRoles } from "../../graphql/rbac/queries";
import { onCreateRole, onUpdateRole, onDeleteRole } from "../../graphql/rbac/subscriptions";
import {Role} from "../../API";

export class AccessRolesDataSource extends DataSource<Role, Role, BasicDataSourceDeps> {
    readonly _modelName = 'Role' as const;
    
    readonly _subscriptionGQLs = {
        onCreate: onCreateRole,
        onUpdate: onUpdateRole,
        onDelete: onDeleteRole,
    };

    constructor(deps: BasicDataSourceDeps) {
        super(deps);
        this.setSortRule('displayName');
    }

    get syncHash() {
        return 'roles';
    }

    get syncQueryFilter() {
        return {};
    }

    getFilterDefaultValues(): {} {
        return {
            name: '',
            workspaceId: null,
        };
    }

    getFilterSchema(): FilterSchema<Role> {
        return {
            name: {
                type: 'client',
                apply: (role: Role) => (
                    this.filterValues.name === undefined
                    || role.displayName?.toLowerCase().indexOf((this.filterValues.name || '').toLowerCase()) !== -1
                ),
            },
            workspaceId: {
                type: 'client',
                apply: (role: Role) => this.filterValues.workspaceId === null || role.workspaceId === this.filterValues.workspaceId,
            },
        };
    }

    async loadGQLFetch() {
        const result = await queryGqlApi(
            listRoles,
            {},
            this.deps.dataClientService,
            this.deps.authService,
            'pgServer'
        );
        
        return {
            items: result.items || [],
            nextToken: result.nextToken || null
        };
    }

    async loadMoreGQLFetch(nextToken: string) {
        const result = await queryGqlApi(
            listRoles,
            { nextToken },
            this.deps.dataClientService,
            this.deps.authService,
            'pgServer'
        );
        
        return {
            items: result.items || [],
            nextToken: result.nextToken || null
        };
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientByKey('pgServer');
    }

    getWsRoles(workspaceId: string): Role[] {
        return this.allRows.filter(role => role.workspaceId === workspaceId);
    }
}
